import { AddressTextField } from '@components/AddressTextField/AddressTextField'
import BottomSheet from '@components/BottomSheet'
import { ContactList } from '@components/ContactList/ContactList'
import ErrorPopup from '@components/ErrorPopup'
import NFTDisplayer from '@components/NftDisplayer'
import { NftList } from '@components/NftList/NftList'
import Picker from '@components/Picker'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { TokenList } from '@components/TokenList/TokenList'
import { TokenListItem } from '@components/TokenList/TokenListItem'
import { useBackAfterSheet } from '@hooks/useBackAfterSheet'
import { getPrincipals } from '@plug-core/handlers/walletActions/wallet'
import useSend, { SendMode } from '@plug-core/hooks/useSend'
import { logger } from '@plug-core/logger'
import { formatNumber } from '@plug-core/utils/formatNumber'
import { e8sToDecimals, sanitizeDecimalInput } from '@plug-core/utils/validationHelper'
import {
  Avatar,
  Banner,
  Button,
  Field,
  Heading,
  Input,
  ListGroup,
  ListRow,
  RowTile,
  Surface,
  Text,
} from '@ui'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaArrowDown, FaArrowUp } from 'react-icons/fa6'
import { useLocation, useNavigate } from 'react-router-dom'
import SendReview from './Components/SendReview'

const Send = () => {
  const [walletPrincipals, setWalletPrincipals] = useState<string[]>([])
  const { t } = useTranslation()

  const { state } = useLocation()

  const mode = state?.topup ? SendMode.CycleTopup : SendMode.Send

  const navigate = useNavigate()
  const backAfterSheet = useBackAfterSheet()

  const {
    addressMatched,
    hasSelectedAsset,
    toAddress,
    setToAddress,
    addressType,
    nft,
    nfts,
    token,
    balancesForSelect,
    onTokenSelect,
    isFindingTokenBalances,
    setIsFindingTokenBalances,
    findTokens,
    setNft,
    amount,
    setAmount,
    maxAmount,
    setSelectedMode,
    amountCurrency,
    feeAmountCurrency,
    setAmountCurrency,
    userLocale,
    xtcSendMode,
    setXtcSendMode,
    fee,
    validateAndShowSummary,
    error,
    summaryVisible,
    setSummaryVisible,
    e8sAmount,
    utxosToUse,
    sendError,
    setSendError,
    wallet,
    onError,
    walletNfts,
    canSendNft,
    showXTCSendMode,
    cyclesOutAmount,
    contactSections,
    receiverChain,
    setContact,
    matchedContact,
    receiverAddress,
  } = useSend({
    walletPrincipals,
    selectedTokenId: state?.selectedTokenId,
    selectedNft: state?.selectedNft,
    mode,
  })

  useEffect(() => {
    getPrincipals()
      .then((principals) => setWalletPrincipals(principals.map((p) => p.toString())))
      .catch(logger.error)
  }, [])

  const ordinals = walletNfts.flatMap((collection) =>
    collection.nfts.filter((nft) => nft.standard === 'ordinal')
  )

  const renderSelectedNft = () => {
    if (!nft || !addressMatched) return

    const collection = nfts.find((c) => c.address === nft.collection)

    return (
      <div className="flex flex-col gap-4">
        <ListGroup>
          <ListRow
            leading={<Avatar size={40} square src={collection?.logo_url || nft?.url} />}
            primary={collection?.name || 'Ordinal'}
            secondary={`#${nft.standard === 'ordinal' ? nft.number : nft.index}`}
          />
        </ListGroup>
        <NFTDisplayer nft={nft} />
      </div>
    )
  }

  const title = mode === SendMode.Send ? t('send.title') : t('send.canisterTopup')

  const showCyclesOut = !!cyclesOutAmount
  const showFee = !!fee && (fee.amount || 0) > 0 && addressMatched && hasSelectedAsset

  return (
    <div className="mb-3 flex h-full flex-col">
      <Titlebar onActionClick={() => navigate(-1)} />

      <div className="flex flex-1 flex-col gap-4">
        <AddressTextField
          isValidAddress={addressMatched}
          toAddress={toAddress}
          setToAddress={setToAddress}
          matchedContact={matchedContact}
          addressType={addressType}
          sendMode={mode}
        />

        {!addressMatched &&
          contactSections.map(
            ({ kind, contacts }) =>
              contacts.length > 0 && (
                <div key={kind} className="flex flex-col gap-1">
                  <Text variant="label" tone="fg-2">
                    {t(`send.contacts.${kind}`)}
                  </Text>
                  <ContactList contacts={contacts} onContactClick={setContact} hideDeleteButton />
                </div>
              )
          )}

        {addressMatched && !hasSelectedAsset && (
          <div className="flex flex-col">
            <Heading level={5} className="mb-2">
              Tokens
            </Heading>

            <TokenList
              balances={balancesForSelect}
              onTokenClick={onTokenSelect}
              showPriceChange={false}
            />

            <div className="mt-4 flex flex-col items-center">
              <Text variant="caption" tone="fg-2" align="center">
                {t('send.noTokens')}
              </Text>

              <Button
                variant="link"
                loading={isFindingTokenBalances}
                disabled={isFindingTokenBalances}
                size="sm"
                onClick={async () => {
                  setIsFindingTokenBalances(true)
                  await findTokens(wallet!.principalId)
                  setIsFindingTokenBalances(false)
                }}
              >
                {t('send.findTokens')}
              </Button>
            </div>
          </div>
        )}

        {canSendNft && (
          <div className="flex flex-col gap-2">
            <Heading level={5}>NFTs</Heading>
            <NftList nftChain={receiverChain} onNftClick={setNft} fromSend />
          </div>
        )}

        {addressMatched && token && (
          <TokenListItem
            balance={token}
            onClick={() => onTokenSelect(undefined)}
            showPriceChange={false}
          />
        )}

        {renderSelectedNft()}

        {addressMatched && token && (
          <Field>
            <Input
              aria-label={t('send.amount')}
              inputMode="decimal"
              placeholder="0.0"
              value={amount}
              onChange={(e) => {
                sanitizeDecimalInput(e.target.value, setAmount)
              }}
              onFocus={() => setSelectedMode('token')}
              autoFocus
              className="tabular"
              trailing={
                <>
                  {typeof maxAmount === 'bigint' && (
                    <button
                      type="button"
                      onClick={() =>
                        setAmount(e8sToDecimals(maxAmount, token.token.decimals).toString())
                      }
                      className="cursor-pointer rounded-xs border-0 bg-surface-2 px-2 py-0.5 font-text text-[12px] font-medium text-fg-2 transition-colors hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                    >
                      MAX
                    </button>
                  )}
                  <Text as="span" variant="body" weight="semibold" tone="fg">
                    {token.token.symbol}
                  </Text>
                </>
              }
            />
          </Field>
        )}

        {addressMatched && token?.tokenPrice && (
          <Input
            inputMode="decimal"
            value={amountCurrency}
            onChange={(e) => sanitizeDecimalInput(e.target.value, setAmountCurrency)}
            onFocus={() => setSelectedMode('currency')}
            className="tabular"
            trailing={
              <Text as="span" variant="body" tone="fg-2">
                {userLocale.currency.symbol}
              </Text>
            }
          />
        )}

        {showXTCSendMode && (
          <Picker
            values={[
              { key: 'burn', humanValue: '🔥 Burn' },
              { key: 'transfer', humanValue: '💸 Transfer' },
            ]}
            currentValue={xtcSendMode}
            onValueChange={setXtcSendMode}
            heading="Select Mode"
          />
        )}

        {utxosToUse.length > 0 && (
          <Surface as="details" padding="none" className="group">
            <summary
              id="utxo-header"
              aria-controls="utxo-content"
              className="flex min-h-12 cursor-pointer list-none items-center justify-between px-4 py-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus [&::-webkit-details-marker]:hidden"
            >
              <Text as="span" variant="body-s" weight="medium">
                {t('send.inputs')}
              </Text>
              <FaArrowDown
                aria-hidden="true"
                className="size-3.5 text-fg-2 transition-transform duration-150 group-open:rotate-180"
              />
            </summary>
            <div id="utxo-content" className="flex flex-col gap-4 px-4 pt-1 pb-2">
              {utxosToUse.map((utxo, index) => {
                const isOrdinal =
                  nft?.standard === 'ordinal' &&
                  nft?.txId === utxo.mintTxid &&
                  nft.mintIndex === utxo.mintIndex
                return (
                  <div key={index} className="flex items-center gap-4">
                    <RowTile className="size-7 text-[14px] text-fg">
                      <FaArrowUp aria-hidden="true" />
                    </RowTile>
                    <Text variant="body" weight="medium" tabular>
                      {(utxo.value / 100_000_000).toFixed(8)} {token?.token.symbol}
                    </Text>
                    {isOrdinal && (
                      <Text variant="caption" tone="warning">
                        {t('send.selectedOrdinal')}
                      </Text>
                    )}
                  </div>
                )
              })}
            </div>
          </Surface>
        )}

        {ordinals.length > 0 && token?.token.chain === 'btc' && addressMatched && (
          <Banner tone="warning" className="mt-4">
            {t('send.excludedOrdinals', {
              ordinals: ordinals.length,
            })}
          </Banner>
        )}

        {(showCyclesOut || showFee) && (
          <ListGroup>
            {showCyclesOut && (
              <ListRow
                primary={t('send.canisterReceives')}
                trailing={
                  <Text as="span" variant="body-s" tone="fg" tabular>
                    {formatNumber(cyclesOutAmount, 'TC')}
                  </Text>
                }
              />
            )}
            {showFee && (
              <ListRow
                primary={t('send.fee')}
                trailing={
                  <span className="flex items-center gap-2">
                    <Text as="span" variant="body-s" tone="fg" tabular>
                      {formatNumber(
                        e8sToDecimals(fee!.amount!, fee!.token.decimals),
                        fee!.token.symbol
                      )}
                    </Text>
                    {feeAmountCurrency && (
                      <Text as="span" variant="body-s" tone="fg-2" tabular>
                        ({feeAmountCurrency})
                      </Text>
                    )}
                  </span>
                }
              />
            )}
          </ListGroup>
        )}

        <div>
          {addressMatched && hasSelectedAsset && (
            <Button variant="primary" onClick={validateAndShowSummary} disabled={!!error} fullWidth>
              {error || title}
            </Button>
          )}
        </div>

        <BottomSheet visible={summaryVisible} onBackdropClick={() => setSummaryVisible(false)}>
          <SendReview
            nft={nft}
            toAddress={receiverAddress}
            token={token}
            amount={e8sAmount}
            cancelAction={() => setSummaryVisible(false)}
            onSuccess={() => {
              setSummaryVisible(false)
              backAfterSheet(
                mode === SendMode.Send ? t('send.review.sent') : t('send.review.toppedUp')
              )
            }}
            onError={onError}
            xtcSendMode={xtcSendMode}
            utxos={utxosToUse}
            fee={fee}
            mode={mode}
          />
        </BottomSheet>
      </div>
      <ErrorPopup
        visible={!!sendError}
        title="Send Error"
        description={sendError}
        onDismiss={() => setSendError(undefined)}
      />
    </div>
  )
}

export default Send
